// src/components/dashboard/DashboardEngagementDetail.tsx
//
// The missing page: a view of a specific COMMITTED ENGAGEMENT, not an
// outcome definition (that's DashboardOutcomeDetail's job). This is
// where EngagementReadinessGate and connector forms (MarqetaConnectForm)
// actually belong — per the reference design (buildlog IxVzKfwsbfK0xZPVDLf8)
// and the later correction that outcome-level was the wrong home
// (buildlog exchange re: DashboardOutcomeDetail having no engagement
// concept at all).
//
// Reachable by BOTH the owner (FDW checking their own client's
// engagement) and the counterparty (the client checking their own) —
// both get_outcome_completion_status and get_engagement_connector_requirements
// are already dual-branch (owner/counterparty), confirmed live-working
// this session. This page doesn't need its own access-control logic —
// it just calls those tools and renders what comes back; the tools
// themselves already resolve who's allowed to see what.
//
// UPDATED — the /engagements/:engagementRef/completion route this page
// depends on was flagged as "NOT independently confirmed to exist" at
// draft time. It has since been built, deployed, and extensively
// live-tested (real delegated-agent tokens, real engagements) in the
// same session that produced this file. That flag is resolved, not a
// remaining risk.
//
// UPDATED — get_engagement_findings now exists (functions/fdwApi/tools/
// get-engagement-findings.ts), unblocked by this session's Step 5
// fan-out work (run-marqeta-detection.ts now writes transactionFindings
// docs with a real engagementRef — the join this tool was previously
// blocked on not existing). The placeholder that previously stood in
// for "nothing real to render here yet" is now a real fetch + render
// against GET /engagements/:engagementRef/findings.
//
// Known, real limitation carried through from get-engagement-findings.ts:
// only ATTRIBUTED findings ever appear here — an engagement with no
// declared card-product scope (record_engagement_scope) will show the
// "nothing yet" empty state even if broader, unattributed anomalies
// exist elsewhere in FDW's detection data. Not a bug in this component;
// a property of what the underlying tool is allowed to return to a
// client-facing caller.
//
// UPDATED — header now shows the real outcome name and scope summary
// instead of the generic "Engagement" heading. Sourced directly from
// the /completion response (get-outcome-completion-status.ts extended
// this session to also return outcomeName/scope/counterpartyLabel,
// fields the underlying engagement doc already carried but this route
// never surfaced). Deliberately NOT passed via React Router route
// state from the list view — this page must render correctly on a
// bare, direct URL (bookmark, refresh, shared link), not only when
// navigated from DashboardEngagements.tsx, so it fetches its own full
// context rather than depending on how the user arrived here.
//
// FIXED (this pass) — the findings/empty-state block inside
// EngagementReadinessGate's render-prop was unconditioned on readiness,
// only on findings.totalFindings. Once EngagementReadinessGate was
// fixed to invoke the render-prop in BOTH the ready and not-ready
// states (a separate, prior fix), this block's "Everything needed to
// start is connected" empty-state text started rendering simultaneously
// alongside the gate's own "This engagement can't start yet — connect
// your marqeta program" blocking message — a real, confirmed,
// contradictory UI state. Fixed by gating the findings/empty-state
// block on `missing.length === 0` (i.e. isReady) as well — it now only
// ever renders once the engagement is actually ready, matching what its
// own copy has always claimed. The connector form
// (MarqetaConnectForm) remains unconditioned on isReady, since
// rendering it specifically in the NOT-ready state is the entire point
// of this component's earlier fix.
//
// [RESOLVED — DashboardEngagements.tsx now exists and links here; an
// earlier version of this comment flagged no navigation existing to
// this page. That gap is closed.]

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { authedFetch } from '../../services/dashboardApi';
import { Loader2Icon, CheckCircle2Icon, ClockIcon, AlertTriangleIcon } from 'lucide-react';
import { EngagementReadinessGate } from './EngagementReadinessGate';
import { MarqetaConnectForm } from './MarqetaConnectForm';

interface CompletionStatus {
  status: 'pending' | 'in_progress' | 'complete';
  description?: string;
  completionCriteriaMet?: boolean;
  outcomeName?: string;
  scope?: string;
  counterpartyLabel?: string | null;
  counterpartyId?: string;
  // resolutionNote deliberately absent from the type — stripped for
  // counterparty callers per get-outcome-completion-status.ts's own
  // design; an owner viewing this same page would see it too if
  // present, this component just doesn't rely on it being there.
}

interface Finding {
  findingId: string;
  status: 'open' | 'resolved' | 'auto_resolved';
  severity: 'info' | 'review_required' | 'escalated';
  summary: string;
  createdAt: string;
  resolvedAt: string | null;
}

interface FindingsResponse {
  outcomeName: string;
  totalFindings: number;
  resolvedFindings: number;
  findings: Finding[];
}

function statusBadge(status: CompletionStatus['status']) {
  if (status === 'complete') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-2 py-0.5 text-xs text-green-700">
        <CheckCircle2Icon className="size-3.5" /> Complete
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-700">
      <ClockIcon className="size-3.5" /> {status === 'in_progress' ? 'In progress' : 'Pending'}
    </span>
  );
}

export const DashboardEngagementDetail: React.FC = () => {
  const { engagementRef } = useParams<{ engagementRef: string }>();
  const [completion, setCompletion] = useState<CompletionStatus | null>(null);
  const [findings, setFindings] = useState<FindingsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadCompletion = async () => {
    if (!engagementRef) return;
    setIsLoading(true);
    setError(null);
    try {
      const res = await authedFetch(`/engagements/${engagementRef}/completion`);
      if (!res.ok) throw new Error('Failed to load this engagement.');
      const data: CompletionStatus = await res.json();
      setCompletion(data);
    } catch (err: any) {
      setError(err.message || 'Could not load this engagement.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadFindings = async () => {
    if (!engagementRef) return;
    try {
      const res = await authedFetch(`/engagements/${engagementRef}/findings`);
      // Non-fatal — findings are supplementary to the completion view.
      // An engagement with no attributed findings yet (or ever, if
      // unscoped) is a normal state, not an error condition for this
      // page as a whole.
      if (!res.ok) return;
      const data: FindingsResponse = await res.json();
      setFindings(data);
    } catch {
      // Non-fatal, same posture as above.
    }
  };

  useEffect(() => {
    loadCompletion();
    loadFindings();
  }, [engagementRef]);

  if (!engagementRef) return <p className="text-sm text-red-600">No engagement specified.</p>;
  if (isLoading) return <p className="text-sm text-brand-gray font-mono">Loading…</p>;
  if (error) return <p className="text-sm text-red-600">{error}</p>;

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold text-brand-black">
            {completion?.outcomeName || 'Engagement'}
          </h1>
          {completion && statusBadge(completion.status)}
        </div>
        <p className="mt-1 text-sm text-brand-gray">
          {completion?.scope || completion?.description || 'Work FDW is doing for you under this engagement.'}
        </p>
        {(completion?.counterpartyLabel || completion?.counterpartyId) && (
          <p className="mt-1 text-xs text-brand-gray">
            With {completion.counterpartyLabel ?? completion.counterpartyId}
          </p>
        )}
      </div>

      {/* The actual connector-readiness gate — this is the piece that
          was previously homeless. Blocks below content until whatever
          this engagement's outcome type requires (e.g. Marqeta) is
          connected. */}
      <EngagementReadinessGate engagementRef={engagementRef}>
        {({ missing }) => {
          const isReady = missing.length === 0;
          return (
            <>
              {isReady && (
                findings && findings.totalFindings > 0 ? (
                  <div className="space-y-2">
                    <p className="text-xs text-brand-gray">
                      {findings.resolvedFindings} of {findings.totalFindings} resolved
                    </p>
                    {findings.findings.map((f) => (
                      <div
                        key={f.findingId}
                        className="rounded-lg border border-brand-border p-3 flex items-start gap-2"
                      >
                        {f.severity === 'escalated' && (
                          <AlertTriangleIcon className="size-4 shrink-0 text-amber-600 mt-0.5" />
                        )}
                        <div>
                          <p className="text-sm text-brand-black">{f.summary}</p>
                          <p className="text-xs text-brand-gray mt-0.5">
                            {f.status === 'open' ? 'Awaiting review' : 'Resolved'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-xl border border-dashed border-brand-border p-6 text-center">
                    <p className="text-sm text-brand-gray">
                      Everything needed to start is connected. Findings and progress will appear here once
                      work begins.
                    </p>
                  </div>
                )
              )}

              {/* Connector management — ONLY the form(s) this specific engagement's
                  outcome actually requires. Renders regardless of isReady — that's
                  precisely the not-ready case's purpose. */}
              {missing.some((r) => r.kind === 'marqeta_connector') && <MarqetaConnectForm />}
            </>
          );
        }}
      </EngagementReadinessGate>
    </div>
  );
};